import { useState } from 'react';

import { ContractMessage, PriceListActivationMode } from '../../../../types/ContractMessage';
import { Button } from '../../../cdl/Button/Button';
import { Label } from '../../../cdl/Label/Label';
import { Modal } from '../../../cdl/Modal/Modal';
import { Description, Radio, RadioGroup, RadioGroupItem } from '../../../cdl/RadioGroup/RadioGroup';
import { Text } from '../../../cdl/Text/Text';
import { useToasts } from '../../../cdl/Toast/useToasts';
import { ButtonGroup } from '../../../common/buttons/ButtonGroup';
import { translate } from '../../../common/helpers/translate.helper';
import { Box } from '../../../common/ui/Box';
import { useContractUpdate } from '../../../contracts/form/hooks/useContractUpdate';

interface ContractPriceListActivationModalProps {
    contract: ContractMessage;
    isOpen: boolean;
    onDismiss: () => void;
}

export const ContractPriceListActivationModal = ({
    contract,
    isOpen,
    onDismiss,
}: ContractPriceListActivationModalProps) => {
    const updateMutation = useContractUpdate();
    const { addToast, addErrorToast } = useToasts();
    const [selectedMode, setSelectedMode] = useState<PriceListActivationMode>(
        contract.priceListActivationMode ?? PriceListActivationMode.AUTO
    );

    const handleSave = async () => {
        try {
            await updateMutation.mutateAsync({
                id: contract.id,
                data: {
                    name: contract.name,
                    customerGroupId: contract.customerGroupId,
                    supplierId: contract.supplierId,
                    customerIds: contract.customerIds,
                    excludedVesselIds: contract.excludedVesselIds,
                    state: contract.state,
                    contractReference: contract.contractReference,
                    description: contract.description,
                    validFrom: contract.validFrom,
                    validUntil: contract.validUntil,
                    evergreen: contract.evergreen,
                    minimumVolume: contract.minimumVolume,
                    paymentTermReference: contract.paymentTermReference,
                    paymentTermReferenceDays: contract.paymentTermReferenceDays,
                    gtc: contract.gtc,
                    priceListActivationMode: selectedMode,
                    effectiveDateMode: contract.effectiveDateMode,
                },
            });
            addToast(translate('contracts.priceLists.activation.toast.success'));
            onDismiss();
        } catch {
            addErrorToast(translate('contracts.priceLists.activation.toast.error'));
        }
    };

    return (
        <Modal isOpen={isOpen} onDismiss={onDismiss} scrollable={false}>
            <Box padding={24} display="grid" rowGap={24}>
                <Text variant="title">{translate('contracts.priceLists.activation.title')}</Text>

                <Box display="grid" rowGap={8}>
                    <Text variant="fieldLabel">{translate('contracts.priceLists.activationMode')}</Text>
                    <RadioGroup
                        value={selectedMode}
                        onValueChange={(value) => setSelectedMode(value as PriceListActivationMode)}
                    >
                        <Box display="grid" rowGap={16}>
                            <RadioGroupItem>
                                <Radio value={PriceListActivationMode.AUTO} />
                                <Label>{translate('contracts.priceLists.activation.options.auto.label')}</Label>
                                <Description>
                                    {translate('contracts.priceLists.activation.options.auto.description')}
                                </Description>
                            </RadioGroupItem>

                            <RadioGroupItem>
                                <Radio value={PriceListActivationMode.MANUAL} />
                                <Label>{translate('contracts.priceLists.activation.options.manual.label')}</Label>
                                <Description>
                                    {translate('contracts.priceLists.activation.options.manual.description')}
                                </Description>
                            </RadioGroupItem>
                        </Box>
                    </RadioGroup>
                </Box>

                <ButtonGroup justifyContent="end">
                    <Button onClick={onDismiss}>{translate('btn.cancel')}</Button>
                    <Button
                        emphasis="high"
                        isLoading={updateMutation.isPending}
                        onClick={handleSave}
                        disabled={selectedMode === (contract.priceListActivationMode ?? PriceListActivationMode.AUTO)}
                    >
                        {translate('btn.save.default')}
                    </Button>
                </ButtonGroup>
            </Box>
        </Modal>
    );
};
